import { axiosInstance } from "@/utils/lib/axios";
import { QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { RFSData } from "./rfs";
import { AfontData, HptarData } from "./gpon";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { Paginated } from "./types";

interface GenerateABDFATData {
  id: string;
  fatId: string;
  poleId: string;
  coordinateLat: string;
  coordinateLong: string;
  poleProvider: string;
  poleType: string;
  generateAbdPole: {
    id: string;
    poleId: string;
    coordinateLat: string;
    coordinateLong: string;
    poleProvider: string;
    poleType: string;
    rfs: RFSData;
  };
  rfs: RFSData;
  findOntSuccess: boolean;
  hptar?: HptarData | null;
  afont?: AfontData | null;
  createdDate?: string | null;
  longitude?: string | null;
  latitude?: string | null;
  distance?: string | null;
  createDate?: string | null;
}

interface InfiniteGenerateABDFATData extends Paginated {
  content: GenerateABDFATData[];
  nextPage: number | null;
}

/* ===================== *
 * READ generate ABD FAT *
 * ===================== */

type GenerateABDFATsQueryKey = DataConfig & {
  rfsId?: string;
};

async function getGenerateABDFATs(
  queryFunctionContext: QueryFunctionContext,
): Promise<GenerateABDFATData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, generateABDFATQueryKey] = queryKey;
  const { rfsId, sort, asc } =
    generateABDFATQueryKey as GenerateABDFATsQueryKey;

  const response = await axiosInstance.get(
    `/api/crud/generate-abd-fat?rfs.id=${rfsId ?? ""}&sort=${
      sort ?? "fatId"
    }&asc=${asc ?? "true"}`,
  );
  return response.data.data.content;
}
function useGenerateABDFATs(
  generateABDFATQueryKey: GenerateABDFATsQueryKey = {},
  options?: {
    enabled?: boolean;
    staleTime?: number;
    keepPreviousData?: boolean;
  },
) {
  const { rfsId } = generateABDFATQueryKey;
  return useQuery({
    ...options,
    queryKey: ["generateABDFATs", { rfsId }],
    queryFn: getGenerateABDFATs,
  });
}

async function getInfiniteGenerateABDFATs(
  queryFunctionContext: QueryFunctionContext,
): Promise<InfiniteGenerateABDFATData> {
  const { queryKey } = queryFunctionContext;
  const [_, generateABDFATQueryKey] = queryKey;
  const {
    rfsId,
    sort,
    asc,
    page = 0,
  } = generateABDFATQueryKey as GenerateABDFATsQueryKey;

  const response = await axiosInstance.get(
    `/api/crud/generate-abd-fat?rfs.id=${rfsId ?? ""}&sort=${
      sort ?? "fatId"
    }&asc=${asc ?? "true"}&page=${page}`,
  );

  return response?.data?.data;
}
function useInfiniteGenerateABDFATs(
  generateABDFATQueryKey: GenerateABDFATsQueryKey = {},
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) {
  const { rfsId, page } = generateABDFATQueryKey;
  return useQuery({
    ...options,
    queryKey: ["generateABDFATs-infinite", { rfsId, page }],
    queryFn: getInfiniteGenerateABDFATs,
  });
}

async function getGenerateABDFATsCount(
  queryFunctionContext: QueryFunctionContext,
): Promise<number> {
  const { queryKey } = queryFunctionContext;
  const [_, generateABDFATQueryKey] = queryKey;
  const { rfsId } = generateABDFATQueryKey as GenerateABDFATsQueryKey;

  const response = await axiosInstance.get(
    `/api/crud/generate-abd-fat/count?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data;
}
function useGenerateABDFATsCount(
  generateABDFATQueryKey: GenerateABDFATsQueryKey = {},
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) {
  const { rfsId } = generateABDFATQueryKey;
  return useQuery({
    ...options,
    queryKey: ["generateABDFATsCount", { rfsId }],
    queryFn: getGenerateABDFATsCount,
  });
}

export type { GenerateABDFATData, InfiniteGenerateABDFATData };
export {
  useGenerateABDFATs,
  useGenerateABDFATsCount,
  useInfiniteGenerateABDFATs,
};
